import { TranscriptionConfig } from './types'
import { Checkbox, NumberSlider, ButtonGroup, Select } from './FormControls'

interface MelodyTabProps {
  config: TranscriptionConfig
  onChange: (config: TranscriptionConfig) => void
}

export function MelodyTab({ config, onChange }: MelodyTabProps) {
  const update = (key: keyof TranscriptionConfig, value: unknown) => {
    onChange({ ...config, [key]: value })
  }

  return (
    <div className="space-y-4">
      <ButtonGroup
        label="Melody Mode"
        value={config.melodyMode}
        options={[
          { value: 'none', label: 'None' },
          { value: 'lead', label: 'Lead Only' },
          { value: 'all', label: 'All Melodic' },
        ]}
        onChange={(v) => update('melodyMode', v)}
        tooltip="Extract melody from melodic stems (piano, other, guitar)"
      />

      {config.melodyMode !== 'none' && (
        <div className="space-y-4 p-3 bg-surface-900/50 border border-white/5 rounded-lg">
          <Select
            label="Melody Register"
            value={config.melodyRegister}
            options={[
              { value: 'lead', label: 'Lead (High Register)' },
              { value: 'mid', label: 'Mid Register' },
              { value: 'bass', label: 'Bass (Low Register)' },
            ]}
            onChange={(v) => update('melodyRegister', v)}
            tooltip="Which register to extract melody from"
          />

          <NumberSlider
            label="Min Confidence"
            value={config.minConfidence}
            onChange={(v) => update('minConfidence', v)}
            min={0.0}
            max={1.0}
            step={0.05}
            allowNull
            nullLabel="Auto"
            tooltip="Minimum confidence threshold for pitch detection"
          />

          <ButtonGroup
            label="CREPE Pitch Detection"
            value={config.crepe}
            options={[
              { value: 'auto', label: 'Auto' },
              { value: 'all', label: 'All' },
              { value: 'none', label: 'None' },
            ]}
            onChange={(v) => update('crepe', v)}
            tooltip="CREPE pitch detection model usage (auto = stems only, all = all tracks)"
          />
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <Checkbox
          label="Split Bass"
          value={config.splitBass}
          onChange={(v) => update('splitBass', v)}
          tooltip="Separate bass notes into dedicated track"
        />

        <Checkbox
          label="Clean MIDI"
          value={config.clean}
          onChange={(v) => update('clean', v)}
          tooltip="Clean up MIDI by removing artifacts"
        />

        <Checkbox
          label="Disable Merge"
          value={config.noMerge}
          onChange={(v) => update('noMerge', v)}
          tooltip="Disable automatic merging of overlapping notes"
        />

        <Checkbox
          label="Split Stems"
          value={config.splitStems}
          onChange={(v) => update('splitStems', v)}
          tooltip="Split melodic stems into separate MIDI tracks"
        />
      </div>

      {config.splitStems && (
        <NumberSlider
          label="Split Threshold"
          value={config.splitThreshold}
          onChange={(v) => update('splitThreshold', v)}
          min={0.0}
          max={1.0}
          step={0.05}
          tooltip="Threshold for splitting stems (0.0 = aggressive, 1.0 = conservative)"
        />
      )}
    </div>
  )
}
